import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { GET_ALL_STATES, GET_CITIES_BY_STATE, GetAllStatesResponse, GetCitiesByStateResponse } from '../../appointment/graphql/queries/getLocationData';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import {useWarehousePickupZones} from "../../../../hooks/useWarehousePickupZones";
import { useTranslation } from 'react-i18next';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface FilterOptions {
  id: string | undefined;
  name: string | undefined;
  warehouseId: number;
  city: string | undefined;
  state: string | undefined;
  contactName: string | undefined;
  phoneNumber: string | undefined;
  zipcode: string | undefined;
  createdAt: [string | null, string | null] | null;
  updatedAt: [string | null, string | null] | null;
  status: number[] | undefined;
  /** 揽收站点 2.0 · 需求点2：按建联状态(link_status 5 态)筛选 */
  linkStatus?: number[];
  standingPickup: boolean | undefined;
  isNew: boolean | undefined;
  continuousFailure: boolean | undefined;
  address: string | undefined;
  vehicleTypeId: number | undefined;
}

const getDefaultUpdatedAtRange = (): [string, string] => {
  const end = dayjs();
  const start = end.subtract(7, 'day');
  return [start.format('YYYY/MM/DD'), end.format('YYYY/MM/DD')];
};

export const getDefaultFilterOptions = (): FilterOptions => ({
  id: undefined,
  name: undefined,
  warehouseId: 0,
  city: undefined,
  state: undefined,
  contactName: undefined,
  phoneNumber: undefined,
  zipcode: undefined,
  createdAt: null,
  updatedAt: getDefaultUpdatedAtRange(),
  status: [1, 2], // 默认选择启用(1)和待审核(2)状态
  standingPickup: undefined,
  isNew: undefined,
  continuousFailure: undefined,
  address: undefined,
  vehicleTypeId: undefined // 默认全部
});

export const DEFAULT_FILTER_OPTIONS: FilterOptions = getDefaultFilterOptions();

interface FilterAreaProps {
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
  /** 揽收站点 2.0 灰度：true=灰度 hub 用建联状态(linkStatus)筛选，false=非灰度 hub 用原记录状态(status)筛选。 */
  isV2?: boolean;
  statusOptions: { label: string; value: number }[];
  linkStatusOptions?: { label: string; value: number }[];
  sortOption?: string;
  onSortChange?: (value: string | undefined) => void;
  sortOptions?: { label: string; value: string }[];
}

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters, isV2 = false, statusOptions, linkStatusOptions = [], sortOption, onSortChange, sortOptions }) => {
  const [cities, setCities] = useState<string[]>([]);
  const { t, i18n } = useTranslation();
  
  // YES/NO 选项
  const YES_NO_OPTIONS = [
    { label: t('enum.yesorNoStatus.yes'), value: true },
    { label: t('enum.yesorNoStatus.no'), value: false }
  ];

  // 获取"全部"的翻译文本
  const allPlaceholder = useMemo(() => {
    const translated = t('components.input.all');
    return translated === 'components.input.all' ? (i18n.language === 'zh' ? '全部' : 'All') : translated;
  }, [t, i18n.language]);

  // Get all states
  const { data: statesData, loading: statesLoading } = useQuery<GetAllStatesResponse>(GET_ALL_STATES, {
    fetchPolicy: 'cache-and-network'
  });

  // Get cities by state
  const { data: citiesData, loading: citiesLoading } = useQuery<GetCitiesByStateResponse>(GET_CITIES_BY_STATE, {
    variables: { state: filters.state },
    skip: !filters.state,
    fetchPolicy: 'cache-and-network'
  });

  // 获取当前的 warehouse 信息和 pickup zone zipcodes
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;
  const { pickupZones, loading: pickupZonesLoading } = useWarehousePickupZones(warehouseId);

  // 获取车型列表
  const { vehicleTypes } = useVehicleTypes();

  // 处理车辆类型选项（添加"全部"选项）
  const vehicleModelOptions = useMemo(() => {
    const options: { label: string; value: number | string }[] = [
      { value: '', label: allPlaceholder }
    ];

    (vehicleTypes || []).forEach(vt => {
      options.push({
        value: vt.id.toString(),
        label: vt.nameEn
      });
    });

    return options;
  }, [vehicleTypes, allPlaceholder]);

  // 处理州选项
  const stateOptions = useMemo(() => {
    return [
      { value: '', label: allPlaceholder },
      ...(statesData?.getAllStates?.map(state => ({ label: state, value: state })) || [])
    ];
  }, [statesData, allPlaceholder]);

  // 处理城市选项
  const cityOptions = useMemo(() => {
    return [
      { value: '', label: allPlaceholder },
      ...cities.map(city => ({ label: city, value: city }))
    ];
  }, [cities, allPlaceholder]);

  // 处理邮编选项
  const zipcodeOptions = useMemo(() => {
    return pickupZones.map(zipcode => ({ label: zipcode, value: zipcode }));
  }, [pickupZones]);

  // Update cities when state changes
  useEffect(() => {
    if (citiesData?.getCitiesByState) {
      setCities(citiesData.getCitiesByState);
    }
  }, [citiesData]);

  // 该组件仅负责渲染和更新 filters（受控）。筛选/重置按钮在父组件中直接调用。

  const handleFilterChange = (name: string, value: any) => {
    // 对于字符串类型的字段，如果值为空字符串，则设置为 undefined
    // 对于布尔类型的字段（standingPickup, isNew, continuousFailure），直接使用传入的值
    // 对于数字类型的字段（vehicleTypeId），直接使用传入的值
    let processedValue = value;

    if (typeof value === 'string' && value.trim() === '') {
      processedValue = undefined;
    }

    // 对于车辆类型，如果是空字符串，设置为 undefined
    if (name === 'vehicleTypeId' && value === '') {
      processedValue = undefined;
    }

    setFilters({ ...filters, [name]: processedValue });
  };

  const handleUpdatedAtChange = (
      dates: [string | null, string | null] | null
  ) => {
    setFilters({...filters, updatedAt: dates});
  };

  // 按列计算“最宽标签”，用于列内对齐（规则见 UI设计规范）
  const column1Labels = [
    t('pickup.cooperationWarehouse.filters.name'),
    t('pickup.cooperationWarehouse.filters.state'),
    t('pickup.cooperationWarehouse.filters.contactName'),
    t('pickup.cooperationWarehouse.filters.standingPickup'),
    t('pickup.cooperationWarehouse.filters.phoneNumber')
  ];

  const column2Labels = [
    t('pickup.cooperationWarehouse.filters.id'),
    t('pickup.cooperationWarehouse.filters.city'),
    t('pickup.cooperationWarehouse.filters.updatedTime'),
    t('pickup.cooperationWarehouse.filters.isNew'),
    t('pickup.cooperationWarehouse.pageDetails.useVehicleModel')
  ];

  const column3Labels = [
    t('pickup.cooperationWarehouse.filters.status'),
    t('pickup.cooperationWarehouse.filters.zipcode'),
    t('pickup.cooperationWarehouse.columns.address'),
    t('pickup.cooperationWarehouse.filters.continuousFailure'),
    t('pickup.appointment.buttons.sort')
  ];

  const sortLabelClassName = getSmartLabelClassName(column3Labels);

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="flex-1">
        {/* 使用 Grid 布局确保列对齐 - 三等分布局 */}
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          {/* 第一列 */}
          <div className="min-w-0">
            <InputFilterItem
              label={t('pickup.cooperationWarehouse.filters.name')}
              value={filters.name || ''}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              spanClassName={getSmartLabelClassName(column1Labels)}
            />
          </div>
          {/* 第二列 */}
          <div className="min-w-0">
            <BatchInputFilterItem
              label={t('pickup.cooperationWarehouse.filters.id')}
              value={filters.id || ''}
              onChange={(e) => handleFilterChange('id', e.target.value)}
              rows={1}
              placeholder={t('pickup.cooperationWarehouse.placeholders.enterMultipleAddressIds') || undefined}
              spanClassName={getSmartLabelClassName(column2Labels)}
            />
          </div>
          {/* 第三列·状态筛选：灰度 hub=建联状态(linkStatus 5 态)；非灰度 hub=原记录状态(status 启用/禁用/待审核) */}
          <div className="min-w-0">
            {isV2 ? (
              <ParcelFilterStyle
                label={t('pickup.cooperationWarehouse.columns.linkStatus')}
                options={linkStatusOptions}
                value={filters.linkStatus?.map(s => s.toString()) || []}
                onChange={(value: string | string[]) => {
                  const values = Array.isArray(value) && value.length > 0
                    ? value.map(v => parseInt(v)).filter(v => !isNaN(v))
                    : undefined;
                  handleFilterChange('linkStatus', values);
                }}
                mode="multiple"
                spanClassName={getSmartLabelClassName(column3Labels)}
                className="w-full"
                placeholder={allPlaceholder}
              />
            ) : (
              <ParcelFilterStyle
                label={t('pickup.cooperationWarehouse.filters.status')}
                options={statusOptions}
                value={filters.status?.map(s => s.toString()) || []}
                onChange={(value: string | string[]) => {
                  const values = Array.isArray(value) && value.length > 0
                    ? value.map(v => parseInt(v)).filter(v => !isNaN(v))
                    : undefined;
                  handleFilterChange('status', values);
                }}
                mode="multiple"
                spanClassName={getSmartLabelClassName(column3Labels)}
                className="w-full"
                placeholder={allPlaceholder}
              />
            )}
          </div>

          {/* 第二行 */}
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.filters.state')}
              options={stateOptions}
              value={filters.state !== undefined && filters.state !== null && filters.state !== '' ? String(filters.state) : ''}
              onChange={(value: string) => {
                // 处理空字符串（选择"全部"时）和有效值
                const stateValue = value === '' ? undefined : value;
                // 在同一状态更新中同时更新 state 和 city
                setFilters({ 
                  ...filters, 
                  state: stateValue,
                  city: undefined // Reset city when state changes
                });
              }}
              spanClassName={getSmartLabelClassName(column1Labels)}
              className="w-full"
              placeholder={allPlaceholder}
              showSearch
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.filters.city')}
              options={cityOptions}
              value={filters.city !== undefined ? String(filters.city) : ''}
              onChange={(value: string) => handleFilterChange('city', value)}
              spanClassName={getSmartLabelClassName(column2Labels)}
              className="w-full"
              placeholder={allPlaceholder}
              disabled={!filters.state}
              showSearch
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.filters.zipcode')}
              options={pickupZones}
              value={filters.zipcode !== undefined ? String(filters.zipcode) : ''}
              onChange={(value: string) => handleFilterChange('zipcode', value || undefined)}
              spanClassName={getSmartLabelClassName(column3Labels)}
              className="w-full"
              placeholder={t('pickup.appointment.placeholders.selectZipcode') || undefined}
              showSearch
              loading={pickupZonesLoading}
              disabled={!currentWarehouse || pickupZones.length === 0}
              notFoundContent={
                !currentWarehouse
                  ? "Please select a warehouse first"
                  : pickupZones.length === 0
                    ? "No pickup zones available"
                    : "No matching zipcodes"
              }
            />
          </div>

          {/* 第三行 */}
          <div className="min-w-0">
            <InputFilterItem
              label={t('pickup.cooperationWarehouse.filters.contactName')}
              value={filters.contactName || ''}
              onChange={(e) => handleFilterChange('contactName', e.target.value)}
              spanClassName={getSmartLabelClassName(column1Labels)}
            />
          </div>
          <div className="min-w-0">
            <DateRangeFilterItem
              label={t('pickup.cooperationWarehouse.filters.updatedTime')}
              value={filters.updatedAt}
              onChange={handleUpdatedAtChange}
              spanClassName={getSmartLabelClassName(column2Labels)}
            />
          </div>
          <div className="min-w-0">
            <InputFilterItem
              label={t('pickup.cooperationWarehouse.columns.address')}
              value={filters.address || ''}
              onChange={(e) => handleFilterChange('address', e.target.value)}
              spanClassName={getSmartLabelClassName(column3Labels)}
            />
          </div>

          {/* 第四行 */}
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.filters.standingPickup')}
              options={YES_NO_OPTIONS}
              value={filters.standingPickup !== undefined ? String(filters.standingPickup) : undefined}
              onChange={(value: any) =>
                handleFilterChange(
                  'standingPickup',
                  value === undefined || value === null || value === ''
                    ? undefined
                    : value === 'true'
                )
              }
              spanClassName={getSmartLabelClassName(column1Labels)}
              className="w-full"
              placeholder={allPlaceholder}
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.filters.isNew')}
              options={YES_NO_OPTIONS}
              value={filters.isNew !== undefined ? String(filters.isNew) : undefined}
              onChange={(value: any) =>
                handleFilterChange(
                  'isNew',
                  value === undefined || value === null || value === ''
                    ? undefined
                    : value === 'true'
                )
              }
              spanClassName={getSmartLabelClassName(column2Labels)}
              className="w-full"
              placeholder={allPlaceholder}
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.filters.continuousFailure')}
              options={YES_NO_OPTIONS}
              value={filters.continuousFailure !== undefined ? String(filters.continuousFailure) : undefined}
              onChange={(value: any) =>
                handleFilterChange(
                  'continuousFailure',
                  value === undefined || value === null || value === ''
                    ? undefined
                    : value === 'true'
                )
              }
              spanClassName={getSmartLabelClassName(column3Labels)}
              className="w-full"
              placeholder={allPlaceholder}
            />
          </div>

          {/* 第五行 */}
          <div className="min-w-0">
            <InputFilterItem
              label={t('pickup.cooperationWarehouse.filters.phoneNumber')}
              value={filters.phoneNumber || ''}
              onChange={(e) => handleFilterChange('phoneNumber', e.target.value)}
              spanClassName={getSmartLabelClassName(column1Labels)}
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pickup.cooperationWarehouse.pageDetails.useVehicleModel')}
              options={vehicleModelOptions}
              value={filters.vehicleTypeId !== undefined ? String(filters.vehicleTypeId) : undefined}
              onChange={(value: string) => handleFilterChange('vehicleTypeId', value ? parseInt(value) : undefined)}
              spanClassName={getSmartLabelClassName(column2Labels)}
              className="w-full"
              placeholder={allPlaceholder}
            />
          </div>
          {/* 排序 */}
          {onSortChange && sortOptions && (
            <div className="min-w-0">
              <ParcelFilterStyle
                label={t('pickup.appointment.buttons.sort')}
                options={sortOptions}
                value={sortOption}
                onChange={onSortChange}
                spanClassName={sortLabelClassName}
                className="w-full"
                placeholder="Select sorting option"
                showSearch
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterArea;
